// Gemeinsamer Aenderungsweg fuer gemischte Aufgabenlisten (Projekt +
// persoenlich): „Meine Aufgaben" und — mit UX-Vorschlag P3 — der Detailbereich
// in „Mein Tag". Aus routes/MyTasks.tsx herausgezogen; das Verhalten fuer die
// bisherigen Aenderungen (erledigt, Status, Prioritaet, Datum) ist unveraendert.
// Neu dazu: Titel und Zustaendige (braucht der P3-Detailbereich; vorher wurden
// sie hier still verworfen, die Listenzeilen haben sie nie geschickt).

import type { StatusCategory, Task } from "@juuplan/shared";
import { api } from "./api";
import {
  projectDateChangeToPatch,
  type StatusOption,
  type TaskRecord,
  type TaskRecordChange,
} from "./taskRecord";
import { togglePersonalTaskDone, updatePersonalTask } from "../store/personalTasks";

/** A project task annotated by GET /api/me/tasks. */
export interface MeTask extends Task {
  project_id: number;
  project_name: string;
  project_color: string | null;
  phase_name: string;
  done_status_id: number | null;
  open_status_id: number | null;
  /** The task's project's full, ordered status set. */
  project_statuses: { id: number; name: string; color: string; category: StatusCategory; sort_order: number }[];
}

export interface ProjectMeta {
  projects: Map<number, { name: string; color: string | null }>;
  statusIds: Map<number, { done: number | null; open: number | null }>;
  statusSet: Map<number, StatusOption[]>;
}

/** Project lookup (name/color) + done/open status ids + full status set per project. */
export function buildProjectMeta(projectTasks: MeTask[]): ProjectMeta {
  const projects = new Map<number, { name: string; color: string | null }>();
  const statusIds = new Map<number, { done: number | null; open: number | null }>();
  const statusSet = new Map<number, StatusOption[]>();
  for (const t of projectTasks) {
    projects.set(t.project_id, { name: t.project_name, color: t.project_color });
    statusIds.set(t.id, { done: t.done_status_id, open: t.open_status_id });
    if (!statusSet.has(t.project_id)) {
      statusSet.set(
        t.project_id,
        (t.project_statuses ?? []).map((s) => ({
          id: s.id,
          name: s.name,
          color: s.color,
          category: s.category,
        }))
      );
    }
  }
  return { projects, statusIds, statusSet };
}

/**
 * Apply one normalized change to one record (no reload). Returns false when
 * the change could not be applied (e.g. no matching project status for done).
 */
export async function applyHubChange(
  record: TaskRecord,
  change: TaskRecordChange,
  meta: ProjectMeta
): Promise<boolean> {
  if (record.source === "personal") {
    if (change.done !== undefined) await togglePersonalTaskDone(record.id, change.done);
    const body: Parameters<typeof updatePersonalTask>[1] = {};
    if (change.title !== undefined) body.title = change.title;
    if (change.assigneeIds !== undefined) body.assignee_ids = change.assigneeIds;
    if (change.priority !== undefined) body.priority = change.priority ?? "medium";
    // Personal tasks carry a single date (`due_date`); the due date wins.
    if (change.dueDate !== undefined || change.startDate !== undefined) {
      body.due_date = change.dueDate !== undefined ? change.dueDate : change.startDate ?? null;
    }
    if (Object.keys(body).length > 0) await updatePersonalTask(record.id, body);
    return true;
  }
  // An explicit status wins over the done/open shortcut.
  if (change.statusId != null) {
    await api(`/api/tasks/${record.id}`, { method: "PATCH", body: { status_id: change.statusId } });
  } else if (change.done !== undefined) {
    const ids = meta.statusIds.get(record.id);
    const target = change.done ? ids?.done : ids?.open;
    if (target == null) return false;
    await api(`/api/tasks/${record.id}`, { method: "PATCH", body: { status_id: target } });
  }
  const body: Record<string, unknown> = {};
  if (change.title !== undefined) body.title = change.title;
  if (change.assigneeIds !== undefined) body.assignee_ids = change.assigneeIds;
  if (change.priority !== undefined) body.priority = change.priority;
  if (Object.keys(body).length > 0)
    await api(`/api/tasks/${record.id}`, { method: "PATCH", body });
  if (change.dueDate !== undefined || change.startDate !== undefined) {
    // Which field to write depends on the task's own dates — see
    // projectDateChangeToPatch (the row reads `deadline ?? end_date`).
    const dates = projectDateChangeToPatch(record.raw as Task, change);
    if (Object.keys(dates).length > 0)
      await api(`/api/tasks/${record.id}`, { method: "PATCH", body: dates });
  }
  return true;
}
